import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, FileDown, Lock } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDES = [
  "Charleston 3-day itinerary — where to stay, eat, and explore",
  "Orlando beyond the theme parks — a full three-day plan",
  "Savannah long-weekend pacing guide",
  "The 90/60/30-day booking timeline, printable",
  "Complete carry-on packing list for a four-day trip",
  "Realistic travel budget worksheet",
];

export default function PlanningKit() {
  const [loading, setLoading] = useState(false);

  const buy = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "planning_kit",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoading(false);
      toast.error("Checkout could not be started. Please try again.");
    }
  };

  return (
    <div data-testid="planning-kit-page">
      <section className="relative grain-overlay overflow-hidden">
        <div className="absolute top-0 right-1/4 h-[380px] w-[380px] rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-44 pb-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>Premium Download</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
              The Premium Travel <span className="italic text-ocean">Planning Kit.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-charcoal/75 leading-relaxed max-w-xl">
              Six of our most-requested planning resources, polished and printable — itineraries, packing
              systems, and the budget worksheet we use ourselves. Pay once, keep forever.
            </p>
            <ul className="mt-8 space-y-3">
              {INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-charcoal/80">
                  <Check className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <button
                onClick={buy}
                disabled={loading}
                data-testid="buy-kit-button"
                className="group inline-flex items-center gap-3 rounded-full bg-navy px-9 py-4 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-ocean disabled:opacity-70"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Lock className="h-4 w-4" />
                )}
                Get the Kit — $19
              </button>
              <div>
                <p className="text-xs text-slateblue flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-gold" />
                  Secure checkout by Stripe
                </p>
                <p className="text-xs text-slateblue mt-1">One-time payment. Instant access.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden border-4 border-white shadow-[0_40px_80px_-24px_rgba(16,42,67,0.35)] rotate-1">
                <img
                  src={IMAGES.planning}
                  alt="Travel documents and a map being planned"
                  className="h-[420px] sm:h-[520px] w-full object-cover"
                  data-testid="kit-image"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl bg-gold text-navy px-6 py-4 shadow-xl -rotate-2">
                <p className="font-mono text-[10px] tracking-[0.28em] uppercase">6 resources inside</p>
                <p className="mt-1 font-serif text-lg flex items-center gap-2">
                  <FileDown className="h-4 w-4" /> Printable PDF pack
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
